import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { contentApi } from '../../../api/swagger'
import { useAppSnackbar } from '../../../hooks/useAppSnackbar'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { FetchLearningCenterContentProgramTypeEnum } from '../../../swagger'
import {
  FilterFileType,
  FilterLanguage,
  LearningCenterTabs,
} from '../../../utils/searchAndFilterEnums'
import {
  LearningCenterCategories,
  useLearningCenterContext,
} from '../../Context/LearningCenterContext'
import { useAuth } from '../../Routes/AuthProvider'

export const useLearningCenterContent = (options: {
  /** If not provided, don't call the endpoint. */
  programType?: FetchLearningCenterContentProgramTypeEnum
  /** If not greater than 0, don't call the endpoint */
}): {
  loadingId: string
  isLoading: boolean
} => {
  const { programType } = options
  const { t } = useTranslation()
  const { handleError } = useAppSnackbar()
  const { featureAbility } = useAuth()
  const {
    searchQuery,
    updateAssets,
    contentLoadingId: loadingId,
    contentType: filetype,
    level,
    selectedTabKey,
    category,
    cycle,
    language,
    includeOnlyFavorites,
    welcomeCenterContent,
  } = useLearningCenterContext()

  const isChallengeTab = selectedTabKey.includes(LearningCenterTabs.Challenge)

  // Ref to track the most up-to-date tab key to avoid applying outdated fetch results
  const activeTabDuringFetchRef = useRef(selectedTabKey)

  useEffect(() => {
    activeTabDuringFetchRef.current = selectedTabKey
  }, [selectedTabKey])

  const fetchLearningCenterContent = async () => {
    if (
      (level === undefined && !welcomeCenterContent && isChallengeTab) ||
      (cycle === undefined && !isChallengeTab && !welcomeCenterContent)
    )
      return

    try {
      const { assets } = await contentApi.fetchLearningCenterContent({
        /**
         * If we're here, this is defined. We prevent calling the endpoint if
         * undefined, unless the level is defined and we're on the Challenge tab.
         */
        programType: (isChallengeTab
          ? level
          : programType) as FetchLearningCenterContentProgramTypeEnum,
        searchQuery: searchQuery ?? undefined,
        filetype: filetype?.includes(FilterFileType.AllTypes)
          ? undefined
          : filetype,
        category: category?.includes(LearningCenterCategories.AllCategories)
          ? undefined
          : category,
        cycle,
        language:
          language === FilterLanguage.AllLanguages ? undefined : language,
        onlyIncludeFavorites: includeOnlyFavorites,
        welcomeCenterContent,
      })

      // if the tab has changed while we were fetching, don't update the assets
      if (activeTabDuringFetchRef.current !== selectedTabKey) return

      updateAssets(assets)
    } catch (e) {
      await handleError(
        e,
        t(
          'Endpoints.FetchLearningCenterContent.Error',
          'An unknown error occurred fetching learning center content.'
        )
      )
    }
  }

  const { triggerFetch, isLoading } = useLoadingContext({
    loadingId,
    asyncFunction: fetchLearningCenterContent,
  })

  useEffect(() => {
    /** Do call the endpoint
     * - if we do have a programType and we aren't on the Challenge tab,
     * - or we do have a challenge level and are on the Challenge tab,
     * - or we don't have a programType and are fetching welcomeCenterContent
     * - if we can access the feature,
     * */
    if (
      ((!!programType && !isChallengeTab) ||
        (!!level && isChallengeTab) ||
        welcomeCenterContent) &&
      featureAbility.can('learningCenter', 'Feature')
    ) {
      triggerFetch()
    }
  }, [
    featureAbility,
    isChallengeTab,
    level,
    loadingId,
    programType,
    welcomeCenterContent,
    cycle,
    searchQuery,
    filetype,
    category,
    language,
    includeOnlyFavorites,
    triggerFetch,
  ])

  return {
    loadingId,
    isLoading,
  }
}
